<template>
  <div
    class="hide-selection-highlight base-checkbox-wrapper d-flex align-center cursor-pointer"
    :disabled
    @click.stop="handleClick"
    @keypress.enter="handleClick"
  >
    <span
      :class="`base-checkbox flex-shrink-0 ${$attrs.questionnaire === undefined ? (indeterminate || !checked ? 'text-gray_400' : 'text-blue_600') : ''}`"
      for="checkbox"
      :dark="dark"
      :checked="checked"
      :indeterminate="indeterminate"
      :disabled="disabled"
      :readonly="readonly"
    >
      <input type="checkbox" aria-label="checkbox" aria-labelledby="checkbox" />
    </span>
    <slot />
  </div>
</template>
<script setup lang="ts">
import { ref, watchPostEffect } from 'vue'

const boundModel = defineModel<boolean | any>({ default: false })

const props = defineProps<{
  multiple?: boolean
  inputValue?: any
  indeterminate?: boolean
  disabled?: boolean
  dark?: boolean
  readonly?: boolean
}>()
const emit = defineEmits(['input', 'shift-key'])
const checked = ref(false)

const handleClick = (e: MouseEvent | KeyboardEvent) => {
  e.preventDefault()
  if (props.disabled || props.readonly) return
  checked.value = !checked.value
  if (props.multiple && props.inputValue !== undefined && Array.isArray(boundModel.value)) {
    if (!checked.value) {
      const targetIndex = boundModel.value?.findIndex((item: any) => item === props.inputValue)
      boundModel.value.splice(targetIndex, 1)
    } else {
      boundModel.value.push(props.inputValue)
    }

    if (e.shiftKey && checked.value) {
      emit('shift-key', props.inputValue)
    }
  } else {
    boundModel.value = checked.value
  }
}

watchPostEffect(() => {
  if (props.multiple && props.inputValue !== undefined) {
    if (boundModel.value && Array.isArray(boundModel.value)) {
      checked.value = boundModel.value.includes(props.inputValue)
    }
  } else {
    checked.value = boundModel.value
  }
})
</script>
<style scoped lang="scss">
.base-checkbox {
  line-height: 0;
  position: relative;
  width: 40px;
  height: 40px;
  display: inline-block;
  cursor: inherit;
  border-radius: 50%;
  margin: -10px;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: rgba($color: #000000, $alpha: 0.1);
    &[dark='true'] {
      background-color: rgba($color: #636262, $alpha: 0.5);
    }
  }

  input {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: inline-block;
  }

  &::before {
    width: 12px;
    height: 6.4px;
    opacity: 0;
    z-index: 1;
  }

  &::after {
    width: 20px;
    height: 20px;
    border: 2px solid rgb(var(--v-theme-slate_500));
    border-radius: 4px;
  }

  &[checked='true']::before {
    opacity: 1;
    transform: rotate(-45deg);
    transform-origin: -25% 120%;
    border-left: 2px solid rgb(var(--v-theme-blue_600));
    border-bottom: 2px solid rgb(var(--v-theme-blue_600));
  }

  &[checked='true']::after {
    border-color: currentColor;
    background-color: rgb(var(--v-theme-white));
    box-shadow: 0px 0px 4px 0px #2e73e4bf;
  }

  &[indeterminate='true']::before {
    opacity: 1;
    height: 0;
    width: 10.4px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-left: none;
    border-bottom: 2px solid rgb(var(--v-theme-slate_500));
  }
  &[indeterminate='true']::after {
    border-color: rgb(var(--v-theme-slate_500));
  }
}
.base-checkbox-wrapper {
  max-width: max-content;
  height: 40px;
  &::selection {
    background-color: transparent;
  }
  &[disabled='true'],
  &[readonly='true'] {
    cursor: default !important;
    pointer-events: none;
  }
  &[disabled='true'] {
    opacity: 0.38;
    color: rgba(var(--v-theme-slate_900));
  }
}
</style>
